import { createContext, useContext, useState, useEffect } from 'react';
import { subscribeToAuthChanges, getUserData } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const unsubscribe = subscribeToAuthChanges(async (user) => {
        try {
          setCurrentUser(user);
          
          if (user) {
            const data = await getUserData(user.uid);
            setUserData(data);
          } else {
            setUserData(null);
          }
        } catch (err) {
          console.error('Auth state change error:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      });

      return unsubscribe;
    } catch (err) {
      console.error('Auth subscription error:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    error
  };

  // Show error state if Firebase failed to initialize
  if (error && !currentUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Configuration Error</h1>
          <p className="text-gray-600 mb-4">
            Unable to connect to the server. Please check that all environment variables are configured correctly.
          </p>
          <p className="text-sm text-gray-400 font-mono">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
